/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  CreateCloudAccountInput,
  useCreateCloudAccountMutation,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import {
  FormDataAws,
  FormDataAzure,
  FormDataGoogle,
  FormDataIbm,
} from "../../AccountCreate/components/formConfig";

type Props = {
  onSuccess: (id: string) => void;
};

export function useCreateCloudAccount({ onSuccess }: Props) {
  const [createCloudAccountMutation, { loading }] =
    useCreateCloudAccountMutation({
      onCompleted: (data) => {
        if (data.createCloudAccount) {
          onSuccess(data.createCloudAccount.id);
        }
      },
      onError: (error) => {
        showError(error.message);
      },
      update(cache, { data }, { variables }) {
        if (data?.createCloudAccount && variables?.input) {
          cache.modify({
            fields: {
              cloudAccounts: (existing) => {
                return {
                  cloudAccounts: [existing, variables.input],
                };
              },
            },
          });

          cache.gc();
        }
      },
    });

  async function createCloudAccountAws(data: FormDataAws) {
    const prepareDataAws = mapCloudAccountDataAws(data);

    createCloudAccountMutation({
      variables: {
        input: prepareDataAws,
      },
    });
  }

  async function createCloudAccountAzure(data: FormDataAzure) {
    const prepareDataAzure = mapCloudAccountDataAzure(data);

    createCloudAccountMutation({
      variables: {
        input: prepareDataAzure,
      },
    });
  }

  async function createCloudAccountGoogle(data: FormDataGoogle) {
    const prepareDataGoogle = await mapCloudAccountDataGoogle(data);

    createCloudAccountMutation({
      variables: {
        input: prepareDataGoogle,
      },
    });
  }

  async function createCloudAccountIbm(data: FormDataIbm) {
    const prepareDataIbm = mapCloudAccountDataIbm(data);

    createCloudAccountMutation({
      variables: {
        input: prepareDataIbm,
      },
    });
  }

  return [
    {
      createCloudAccountAws,
      createCloudAccountAzure,
      createCloudAccountGoogle,
      createCloudAccountIbm,
    },
    loading,
  ] as const;
}

export function mapCloudAccountDataAws(
  data: FormDataAws
): CreateCloudAccountInput {
  return {
    credentials: {
      aws: {
        rolesChain: [
          {
            arn: data.roleArn,
            externalId: data.roleExternalId,
          },
        ],
      },
    },
    name: data.displayName || data.providerId,
    description: data.description || undefined,
    environment: data.environment || undefined,
    provider: data.provider,
    providerId: data.providerId,
    regions: data.regions.map(({ value }) => value),
  };
}

export function mapCloudAccountDataAzure(
  data: FormDataAzure
): CreateCloudAccountInput {
  return {
    credentials: {
      azure: {
        appId: data.appId,
        password: data.password,
        tenantId: data.tenantId,
      },
    },
    name: data.displayName || data.subscriptionId,
    description: data.description || undefined,
    environment: data.environment || undefined,
    provider: data.provider,
    providerId: data.subscriptionId,
  };
}

export function mapCloudAccountDataGoogle(
  data: FormDataGoogle
): Promise<CreateCloudAccountInput> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    const handleLoad = (readerEvent: ProgressEvent<FileReader>) => {
      const keyFileContent = readerEvent.target?.result as string;
      resolve({
        credentials: {
          gc: {
            keyFile: keyFileContent,
          },
        },
        name: data.displayName || data.projectId,
        description: data.description || undefined,
        environment: data.environment || undefined,
        provider: data.provider,
        providerId: data.projectId,
        regions: data.regions.map(({ value }) => value),
      });
      reader.removeEventListener("load", handleLoad);
      reader.removeEventListener("error", handleError);
    };

    const handleError = () => {
      reject(new Error("Failed to read file"));
      reader.removeEventListener("load", handleLoad);
      reader.removeEventListener("error", handleError);
    };

    reader.addEventListener("load", handleLoad);
    reader.addEventListener("error", handleError);
    reader.readAsText(data.jsonFile);
  });
}

export function mapCloudAccountDataIbm(
  data: FormDataIbm
): CreateCloudAccountInput {
  return {
    credentials: {
      ibm: {
        apiKey: data.apiKey,
      },
    },
    name: data.displayName || data.providerId,
    description: data.description || undefined,
    environment: data.environment || undefined,
    provider: data.provider,
    providerId: data.providerId,
    regions: data.regions.map(({ value }) => value),
  };
}
